import React, { useEffect } from 'react';
import { X, Download, AlertCircle } from 'lucide-react';

interface DocumentViewerProps {
  document: any;
  onClose: () => void;
}

const officeExtensions = ['docx', 'xlsx', 'pptx'];
const googleExtensions = ['doc', 'xls', 'ppt', 'pdf'];

const getExtension = (fileName?: string) =>
  (fileName || '').split('?')[0].split('.').pop()?.toLowerCase() || '';

const getFileName = (doc: any) =>
  doc?.fileName ||
  doc?.file_name ||
  doc?.name ||
  doc?.title ||
  doc?.fileUrl?.split('/').pop() ||
  'document';

const getFileUrl = (doc: any) => doc?.fileUrl || doc?.file_url || doc?.url || '';

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ document: doc, onClose }) => {
  const fileUrl = getFileUrl(doc);
  const fileName = getFileName(doc);
  const extension = getExtension(fileName);
  const encodedUrl = encodeURIComponent(fileUrl);

  const previewUrl = (() => {
    if (!fileUrl) return null;
    if (officeExtensions.includes(extension)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
    }
    if (googleExtensions.includes(extension)) {
      return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
    }
    return fileUrl;
  })();

  const handleDownload = async () => {
    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose]);

  if (!previewUrl) {
    return (
      <div className="fixed inset-0 bg-black/90 z-[99999] flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4 text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Document preview unavailable</h3>
            <p className="text-gray-600">We could not generate a preview for this file type. Please download the document instead.</p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-[#EB1C37] text-white rounded-md hover:bg-[#c73d2a]"
            >
              Download
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/95 z-[99999] flex flex-col">
      <div className="flex items-center justify-between p-4 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-3 text-white">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded transition-colors"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div>
            <p className="font-semibold">{fileName}</p>
            <p className="text-sm text-gray-400 uppercase">{extension || 'Document'}</p>
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 bg-[#EB1C37] text-white rounded hover:bg-[#c73d2a] transition-colors"
        >
          <Download className="h-4 w-4" />
          Download
        </button>
      </div>
      <div className="flex-1 bg-gray-900">
        <iframe
          src={previewUrl}
          title={fileName}
          className="w-full h-full border-0"
        />
      </div>
    </div>
  );
};

export default DocumentViewer;

